/**
 * Joining the business manifest to the geometry, and reporting how well they agree.
 *
 * The manifest and the GLB are maintained independently: the GLB is regenerated whenever the CAD
 * model changes, the manifest is edited by hand. They drift. This module makes that drift
 * explicit instead of silently dropping parts, so a converted model can be checked against its
 * manifest before anyone wonders why a part has no details.
 */

import {
  CAD_PART_DEFAULT_EXPLODE_DISTANCE,
  CAD_PART_DEFAULT_FIXED,
  CAD_PART_DEFAULT_SELECTABLE,
  CadModelManifest,
  CadPartManifestEntry,
  CadViewerPart,
} from '@shared/models/cad_model';

export interface ManifestMappingDiagnostic {
  /** Manifest entries whose `nodeName` matched a node in the GLB. */
  matchedCount: number;
  /** `nodeName`s referenced by the manifest but absent from the GLB. */
  missingNodeNames: string[];
  /** Part nodes present in the GLB that no manifest entry describes. */
  unmappedNodeNames: string[];
  /** Node names appearing more than once in the GLB: selection by name would be ambiguous. */
  duplicateNodeNames: string[];
  /** `id`s used by more than one manifest entry. */
  duplicateManifestIds: string[];
  /** `nodeName`s used by more than one manifest entry. */
  duplicateManifestNodeNames: string[];
}

export function isManifestMappingClean(diagnostic: ManifestMappingDiagnostic): boolean {
  return (
    diagnostic.missingNodeNames.length === 0 &&
    diagnostic.duplicateNodeNames.length === 0 &&
    diagnostic.duplicateManifestIds.length === 0 &&
    diagnostic.duplicateManifestNodeNames.length === 0
  );
}

function duplicatesOf(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    }
    seen.add(value);
  }
  return [...duplicates];
}

/** Compares the manifest with the part node names found in the GLB. */
export function diagnoseManifestMapping(
  manifestParts: readonly CadPartManifestEntry[],
  glbNodeNames: readonly string[]
): ManifestMappingDiagnostic {
  const glbNameSet = new Set(glbNodeNames);
  const manifestNodeNames = manifestParts.map(p => p.nodeName);
  const manifestNodeNameSet = new Set(manifestNodeNames);

  const missingNodeNames: string[] = [];
  let matchedCount = 0;
  for (const nodeName of manifestNodeNameSet) {
    if (glbNameSet.has(nodeName)) {
      matchedCount++;
    } else {
      missingNodeNames.push(nodeName);
    }
  }

  return {
    matchedCount,
    missingNodeNames,
    unmappedNodeNames: [...new Set(glbNodeNames)].filter(n => !manifestNodeNameSet.has(n)),
    duplicateNodeNames: duplicatesOf(glbNodeNames),
    duplicateManifestIds: duplicatesOf(manifestParts.map(p => p.id)),
    duplicateManifestNodeNames: duplicatesOf(manifestNodeNames),
  };
}

function toViewerPart(entry: CadPartManifestEntry): CadViewerPart {
  const part: CadViewerPart = {
    id: entry.id,
    nodeName: entry.nodeName,
    label: entry.label,
    selectable: entry.selectable ?? CAD_PART_DEFAULT_SELECTABLE,
    fixed: entry.fixed ?? CAD_PART_DEFAULT_FIXED,
    explodeDistance: entry.explodeDistance ?? CAD_PART_DEFAULT_EXPLODE_DISTANCE,
    hasManifestEntry: true,
  };
  // Optional fields are only set when present, so the details panel can rely on `undefined`
  // meaning "no data available" rather than "empty string".
  if (entry.reference !== undefined) {
    part.reference = entry.reference;
  }
  if (entry.category !== undefined) {
    part.category = entry.category;
  }
  if (entry.description !== undefined) {
    part.description = entry.description;
  }
  if (entry.dataKey !== undefined) {
    part.dataKey = entry.dataKey;
  }
  if (entry.explodeVector !== undefined) {
    part.explodeVector = entry.explodeVector;
  }
  return part;
}

/**
 * Builds the viewer's part list from the manifest and the GLB's part node names.
 *
 * Node order follows the GLB, so the list matches what the user sees. Nodes with no manifest
 * entry are still listed, labelled with their raw node name, so that a freshly converted model is
 * usable before anyone has written its manifest. Manifest entries pointing at a missing node are
 * dropped from the list and reported in the diagnostic.
 */
export function resolveViewerParts(
  manifest: CadModelManifest | undefined,
  glbNodeNames: readonly string[]
): {parts: CadViewerPart[]; diagnostic: ManifestMappingDiagnostic} {
  const manifestParts = manifest?.parts ?? [];
  const diagnostic = diagnoseManifestMapping(manifestParts, glbNodeNames);

  const byNodeName = new Map<string, CadPartManifestEntry>();
  for (const entry of manifestParts) {
    // First entry wins for a duplicated nodeName; the duplicate is reported in the diagnostic.
    if (!byNodeName.has(entry.nodeName)) {
      byNodeName.set(entry.nodeName, entry);
    }
  }

  const usedIds = new Set<string>();
  const parts: CadViewerPart[] = [];
  for (const nodeName of glbNodeNames) {
    const entry = byNodeName.get(nodeName);
    const part =
      entry !== undefined
        ? toViewerPart(entry)
        : {
            id: nodeName,
            nodeName,
            label: nodeName,
            selectable: CAD_PART_DEFAULT_SELECTABLE,
            fixed: CAD_PART_DEFAULT_FIXED,
            explodeDistance: CAD_PART_DEFAULT_EXPLODE_DISTANCE,
            hasManifestEntry: false,
          };
    // Ids must stay unique for React keys and for selection to be unambiguous.
    if (usedIds.has(part.id)) {
      let suffix = 2;
      while (usedIds.has(`${part.id}__${suffix}`)) {
        suffix++;
      }
      part.id = `${part.id}__${suffix}`;
    }
    usedIds.add(part.id);
    parts.push(part);
  }

  return {parts, diagnostic};
}

/** Node name -> business part id, used to resolve a raycast hit to a part. */
export function buildNodeNameToPartId(parts: readonly CadViewerPart[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const part of parts) {
    if (!map.has(part.nodeName)) {
      map.set(part.nodeName, part.id);
    }
  }
  return map;
}
